import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import {
    Alert,
    Box,
    Button,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemText,
    Stack,
    Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import AuthSplitLayout from './AuthSplitLayout';
import { getOAuthQueryFromSearch } from './auth-utils';
import { betterAuthClient } from '@/libs/better-auth-client';

const SCOPE_LABELS: Record<string, string> = {
    openid: 'auth.oauth.scope.openid',
    profile: 'auth.oauth.scope.profile',
    email: 'auth.oauth.scope.email',
    offline_access: 'auth.oauth.scope.offline-access',
    'read:workspace': 'auth.oauth.scope.read-workspace',
    'write:workspace': 'auth.oauth.scope.write-workspace',
    'read:analytics': 'auth.oauth.scope.read-analytics',
};

const getScopesFromQuery = (): string[] => {
    const params = new URLSearchParams(window.location.search);
    return (params.get('scope') || '')
        .split(' ')
        .map((scope) => scope.trim())
        .filter(Boolean);
};

const getClientIdFromQuery = (): string | null => {
    const params = new URLSearchParams(window.location.search);
    const value = params.get('client_id') || '';
    return value.trim() || null;
};

type BetterAuthResult<TData> = {
    data?: TData | null;
    error?: {
        message?: string;
    } | null;
};

export default function AuthConsent() {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();

    const clientId = useMemo(() => getClientIdFromQuery(), []);
    const requestedScopes = useMemo(() => getScopesFromQuery(), []);
    // The signed OAuth query from the consent redirect URL. The /oauth2/consent endpoint verifies it
    // and rebuilds the pending authorization (oAuthState) from it — without it the server responds
    // 400 "missing oauth query".
    const oauthQuery = useMemo(() => getOAuthQueryFromSearch(), []);

    const [loadingClient, setLoadingClient] = useState(false);
    const [clientName, setClientName] = useState<string | null>(null);
    const [clientUri, setClientUri] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState<'accept' | 'deny' | null>(null);

    useEffect(() => {
        if (!clientId) {
            return;
        }

        setLoadingClient(true);
        void betterAuthClient.oauth2
            .publicClient({ query: { client_id: clientId } })
            .then((result: BetterAuthResult<{ client_name?: string; client_uri?: string }>) => {
                const data = result.data;
                setClientName(data?.client_name || clientId);
                setClientUri(data?.client_uri || null);
            })
            .catch(() => {
                setClientName(clientId);
            })
            .finally(() => {
                setLoadingClient(false);
            });
    }, [clientId]);

    const onDecision = async (accept: boolean) => {
        setSubmitting(accept ? 'accept' : 'deny');
        try {
            const result: BetterAuthResult<{ url?: string }> = await betterAuthClient.oauth2.consent({
                accept,
                scope: requestedScopes.length ? requestedScopes.join(' ') : undefined,
                ...(oauthQuery ? { oauth_query: oauthQuery } : {}),
            });

            if (result.error) {
                enqueueSnackbar(
                    typeof result.error.message === 'string'
                        ? result.error.message
                        : t('auth.oauth.error.default'),
                );
                return;
            }

            const redirectUrl = result.data?.url;
            if (redirectUrl) {
                window.location.href = redirectUrl;
                return;
            }

            enqueueSnackbar(t('auth.oauth.error.redirect-missing'));
        } catch {
            enqueueSnackbar(t('auth.oauth.error.default'));
        } finally {
            setSubmitting(null);
        }
    };

    return (
        <AuthSplitLayout>
            <Stack spacing={3}>
                <Box>
                    <Typography
                        component={Link}
                        to="/auth/login"
                        variant="body2"
                        color="text.secondary"
                        sx={{
                            textDecoration: 'none',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            '&:hover': { color: 'primary.main' },
                        }}
                    >
                        <ArrowBackRounded sx={{ fontSize: 16 }} />
                        {t('auth.oauth.back-to-login')}
                    </Typography>
                </Box>

                <Box>
                    <Typography variant="h5" fontWeight={700} mb={0.5}>
                        {t('auth.oauth.title')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {t('auth.oauth.subtitle')}
                    </Typography>
                </Box>

                {!clientId && (
                    <Alert severity="error">{t('auth.oauth.error.missing-client-id')}</Alert>
                )}

                {clientId && (
                    <Stack spacing={1.5}>
                        <Typography variant="body2" color="text.secondary">
                            {t('auth.oauth.requesting-app')}
                        </Typography>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                            <Chip
                                color="primary"
                                variant="outlined"
                                label={loadingClient ? t('auth.common.loading') : clientName || clientId}
                            />
                            {clientUri && (
                                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                                    {clientUri}
                                </Typography>
                            )}
                        </Stack>
                    </Stack>
                )}

                <Divider />

                <Box>
                    <Typography variant="subtitle2" fontWeight={700} mb={1}>
                        {t('auth.oauth.permissions-title')}
                    </Typography>
                    <List dense disablePadding>
                        {requestedScopes.length > 0 ? (
                            requestedScopes.map((scope) => (
                                <ListItem key={scope} disableGutters>
                                    <ListItemText
                                        primary={t(SCOPE_LABELS[scope] || 'auth.oauth.scope.custom', { scope })}
                                    />
                                </ListItem>
                            ))
                        ) : (
                            <ListItem disableGutters>
                                <ListItemText primary={t('auth.oauth.no-scope-requested')} />
                            </ListItem>
                        )}
                    </List>
                </Box>

                <Stack spacing={1.5}>
                    <Button
                        variant="contained"
                        size="large"
                        fullWidth
                        disabled={!clientId || submitting !== null}
                        onClick={() => onDecision(true)}
                    >
                        {submitting === 'accept' ? t('auth.common.loading') : t('auth.oauth.allow')}
                    </Button>
                    <Button
                        variant="outlined"
                        size="large"
                        fullWidth
                        color="inherit"
                        disabled={!clientId || submitting !== null}
                        onClick={() => onDecision(false)}
                    >
                        {submitting === 'deny' ? t('auth.common.loading') : t('auth.oauth.deny')}
                    </Button>
                </Stack>
            </Stack>
        </AuthSplitLayout>
    );
}
